import { useState, useEffect, useCallback } from 'react'; // v2 - force clean refresh
import { useAuthStore } from '@/stores/authStore';
import { useImpersonationStore } from '@/stores/impersonationStore';
import { getUserRoles, getRolePermissions, type UserRoleType } from '@/services/data/roles';

interface ServiceAccess {
  [service: string]: boolean;
}

export function useServiceAccess() {
  // Get both stores directly to avoid hook ordering issues
  const { userId } = useAuthStore();
  const impersonationState = useImpersonationStore();
  
  // Compute effective user ID safely
  const effectiveUserId = impersonationState?.isImpersonating && impersonationState?.impersonatedUser
    ? impersonationState.impersonatedUser.id
    : userId;
    
  const [serviceAccess, setServiceAccess] = useState<ServiceAccess>({});
  const [isStaff, setIsStaff] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchAccess = useCallback(async () => {
    if (!effectiveUserId) {
      setServiceAccess({});
      setIsStaff(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    // Get user's roles (for the effective user, which may be impersonated)
    const rolesResult = await getUserRoles(effectiveUserId);

    if (!rolesResult.success) {
      console.error('Error fetching user roles:', rolesResult.error);
      setIsLoading(false);
      return;
    }

    const roles: UserRoleType[] = rolesResult.data || [];
    if (roles.length === 0) {
      roles.push('customer');
    }
    setIsStaff(roles.some(r => r !== 'customer'));

    // Get permissions for all user's roles
    const permissionsResult = await getRolePermissions(roles);

    if (!permissionsResult.success) {
      console.error('Error fetching permissions:', permissionsResult.error);
      setIsLoading(false);
      return;
    }

    const permissions = permissionsResult.data || [];
    console.log('[useServiceAccess] Effective user ID:', effectiveUserId);
    console.log('[useServiceAccess] User roles:', roles);
    console.log('[useServiceAccess] Permissions:', permissions);

    // Build access map - user has access if ANY of their roles grants it
    const accessMap: ServiceAccess = {};
    permissions.forEach(p => {
      if (p.has_access) {
        accessMap[p.service] = true;
      } else if (!(p.service in accessMap)) {
        accessMap[p.service] = false;
      }
    });

    setServiceAccess(accessMap);
    setIsLoading(false);
  }, [effectiveUserId]);

  useEffect(() => {
    fetchAccess();
  }, [fetchAccess, refreshKey]);

  const hasAccess = (service: string): boolean => {
    return serviceAccess[service] ?? false;
  };

  const refetch = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  return { serviceAccess, hasAccess, isLoading, isStaff, refetch };
}
